import React, { useContext, useEffect, useRef, useState } from "react";
import {
  notifyError,
  NumberFormatter,
  roundToThreeDecimal,
} from "../../../../../../helpers/helpers";
import { globalContext } from "../../../../../../context/ContextProvider";
import { useDispatch, useSelector } from "react-redux";
import { getSocket } from "../../../../../../helpers/SingletonSocketService";
import ConnectionStatus from "../../children/ConnectionStatus";
import { ClipLoader } from "react-spinners";
import { useParams } from "react-router-dom";
import { postLiveRoomDataForProduct } from "../../../../../../redux/action/liveRoom";

function ProductMobileBiddingArea({
  liveOfferData,
  timeEnded,
  startTimer,
  setBidderDetails,
}) {
  const { id } = useParams();
  const dispatch = useDispatch();
  const inputRef = useRef(null);
  const [bidAmount, setBidAmount] = useState("");
  const [bidPriceUpdateValue, setBidPriceUpdateValue] = useState({});
  const [myCurrentBid, setMyCurrentBid] = useState(0);

  const { profile } = useSelector((state) => state?.profileStore);
  const { loading, loadingId } = useSelector((state) => state.liveAuctionList);
  const { connectingWithLiveRoom } = useContext(globalContext);

  //handle submit bid functionality for live room
  const handleBidUpdateValue = async (e) => {
    e.preventDefault();
    const FirstBidTime =
      bidPriceUpdateValue?.mimumestBid === 0
        ? liveOfferData?.startingPrice
        : bidPriceUpdateValue?.mimumestBid;
    const value = Number(bidAmount);
    const { minimumBidDecrease } = liveOfferData;
    const difference = +(value - FirstBidTime).toFixed(10);
    const multiplied = +(difference / minimumBidDecrease).toFixed(10);
    const nearestInteger = Math.round(multiplied);
    const remainder = Math.abs(multiplied - nearestInteger);
    const tolerance = 0.0001;

    if (timeEnded) {
      return notifyError("Time has ended");
    } else if (startTimer) {
      return notifyError("Auction for this lot has not started yet");
    } else if (
      bidPriceUpdateValue?.mimumestBid !== 0 &&
      value >
        +(
          bidPriceUpdateValue?.mimumestBid -
          (liveOfferData?.minimumBidDecrease || 0)
        ).toFixed(10)
    ) {
      return notifyError(
        `Bid amount should be less than highest bid minus minimum bid decrement. Maximum amount is ${+(
          (bidPriceUpdateValue?.mimumestBid || 0) -
          (liveOfferData?.minimumBidDecrease || 0)
        ).toFixed(10)}`
      );
    } else if (
      value >
      +(
        liveOfferData?.startingPrice - (liveOfferData?.minimumBidDecrease || 0)
      ).toFixed(10)
    ) {
      return notifyError(
        `Bid amount should be less than Starting price minus minimum bid decrement. Maximum amount is ${+(
          liveOfferData?.startingPrice -
          (liveOfferData?.minimumBidDecrease || 0)
        ).toFixed(10)}`
      );
    } else if (Math.abs(remainder) > tolerance) {
      const { minimumBidDecrease } = liveOfferData;
      const nearestMultiple = Math.ceil(difference / minimumBidDecrease);
      const nearestValidBid =
        FirstBidTime + nearestMultiple * minimumBidDecrease;
      const previousValidBid = nearestValidBid - minimumBidDecrease;

      const isDecimal = !Number.isInteger(minimumBidDecrease);
      const format = (num) => (isDecimal ? num.toFixed(2) : num.toFixed(0));

      return notifyError(
        `Bid amount should be in multiples of ₹${format(
          minimumBidDecrease
        )}. ` +
          `Nearest valid bid is ₹${format(nearestValidBid)} or ₹${format(
            previousValidBid
          )}`
      );
    } else {
      dispatch(
        postLiveRoomDataForProduct(value, true, id, liveOfferData?._id)
      ).then(() => {
        inputRef.current?.blur();
      });
    }
  };

  //socket connection for live room
  useEffect(() => {
    const socket = getSocket();
    socket?.on("newBid", (data) => {
      if (data?.offerId === liveOfferData?._id) {
        const { bidHistory, ...restData } = data;
        setBidPriceUpdateValue(restData);
        setBidderDetails([
          {
            myBidColor:
              data?.bidDetails?.organization === profile?.organization,
            bidHistory,
          },
        ]);
      }
    });

    document.body.style.overflow = "auto";
  }, [liveOfferData, profile, setBidderDetails]);

  //find the current bid of the user after refesh the page
  useEffect(() => {
    if (liveOfferData) {
      setBidPriceUpdateValue({
        mimumestBid: liveOfferData?.mimumestBid || 0,
        offerId: liveOfferData?._id,
        myLastBid: liveOfferData?.myCurrentBidAmount,
        bidDetails: {
          organization: profile?.organization,
          bidAmount: liveOfferData?.myCurrentBidAmount,
          autoBidAmount: null,
        },
      });
      setBidderDetails([
        { myBidColor: true, bidHistory: liveOfferData?.bidHistory },
      ]);
    }
  }, [liveOfferData, profile, setBidderDetails]);

  //find the current bid of the user
  useEffect(() => {
    if (
      profile?.organization === bidPriceUpdateValue?.bidDetails?.organization
    ) {
      setMyCurrentBid(bidPriceUpdateValue?.myLastBid);
    }
  }, [bidPriceUpdateValue, profile]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
    }
  };

  useEffect(() => {
    if (bidPriceUpdateValue?.mimumestBid === 0) {
      setBidAmount(liveOfferData?.startingPrice);
    } else {
      setBidAmount(bidPriceUpdateValue?.mimumestBid);
    }
  }, [bidPriceUpdateValue, liveOfferData, profile, myCurrentBid]);

  if (connectingWithLiveRoom) {
    return <ConnectionStatus />;
  }

  return (
    <div className="mobile-bid border-1px full-width flex column gap-10px">
      <div className="valign-wrapper space-between">
        <span
          className="valign-wrapper justify-center small-text"
          style={{
            border: "1px solid #90cb7b",
            padding: "0px 8px",
            borderRadius: "25px",
            color: "#90cb7b",
            backgroundColor: "#e3ffd9",
            gap: "5px",
            marginLeft: "4px",
          }}
        >
          <p
            className="circle"
            style={{
              width: "7px",
              height: "7px",
              backgroundColor: "#90cb7b",
            }}
          ></p>
          <p className="small-text">Live</p>
        </span>
        <p
          className="white-text small-text"
          style={{
            backgroundImage: "linear-gradient(to right, #6F2DA8, #2C1242)",
            padding: "2px 5px",
            borderBottomLeftRadius: "10px",
          }}
        >
          Reverse Product Auction
        </p>
      </div>
      <div>
        <span
          className="valign-wrapper column justify-center gap-10px"
          style={{ gap: "6px" }}
        >
          <h5
            className={`font-18px margin-0px cercle-purple-text valign-wrapper gap-10px`}
          >
            <span>Lowest Bid</span>
            <span className="material-icons-outlined black-text font-20px">
              arrow_upward
            </span>
          </h5>
          <h6
            className={`white black-text border-1px flex justify-center font-18px margin-0px`}
            style={{
              padding: "10px 30px",
              minWidth: "150px",
            }}
          >
            {NumberFormatter(bidPriceUpdateValue?.mimumestBid || 0)}
          </h6>
        </span>
      </div>
      <div
        className="flex column align-center gap-10px"
        style={{ padding: "5px" }}
      >
        <h5 className={`margin-0px font-18px`}>Enter your bid</h5>
        <form
          className="full-width column flex align-center gap-1 justify-center"
          onSubmit={(e) => handleBidUpdateValue(e)}
        >
          <div className="flex" style={{ gap: "5px" }}>
            <button
              type="button"
              className="circle valign-wrapper justify-center pointer circle"
              style={{
                width: "30px",
                height: "30px",
                marginTop: "8px",
                backgroundImage:
                  bidPriceUpdateValue?.mimumestBid === bidAmount ||
                  liveOfferData?.startingPrice === bidAmount
                    ? "linear-gradient(rgb(150, 150, 150),rgb(230, 225, 235))"
                    : "linear-gradient(#4C2171, #6F2DA8)",
              }}
              disabled={timeEnded}
              onKeyDown={handleKeyDown}
              onClick={() =>
                setBidAmount((prev) => {
                  const current = Number(prev) || 0;
                  const increment = liveOfferData?.minimumBidDecrease;
                  const maximumBidAllowed =
                    bidPriceUpdateValue?.mimumestBid === 0
                      ? liveOfferData?.startingPrice
                      : bidPriceUpdateValue?.mimumestBid;
                  const updated = +(current + increment).toFixed(10);
                  return Math.min(updated, maximumBidAllowed);
                })
              }
            >
              <span
                className="white-text"
                style={{ fontSize: "30px", fontWeight: 600 }}
              >
                +
              </span>
            </button>
            <span className="border-radius-12 flex column valign-wrapper justify-center">
              <input
                ref={inputRef}
                value={bidAmount}
                type="number"
                step="0.01"
                min="0"
                disabled={timeEnded}
                className="browser-default white font-18px"
                onChange={(e) => setBidAmount(e.target.value)}
                style={{
                  maxWidth: "120px",
                  boxShadow:
                    "-4px -5px 10px 0 white inset, 3px 3px 8px 0 rgba(0, 0, 0, 0.25) inset",
                  height: "45px",
                  fontWeight: "600",
                  borderRadius: 10,
                  padding: "12px 12px 12px 15px",
                  textAlign: "center",
                }}
              />
            </span>
            <button
              type="button"
              onKeyDown={handleKeyDown}
              className="circle valign-wrapper justify-center pointer"
              style={{
                width: "30px",
                height: "30px",
                marginTop: "8px",
                backgroundImage: "linear-gradient(#4C2171, #6F2DA8)",
              }}
              disabled={timeEnded}
              onClick={() =>
                setBidAmount((prev) => {
                  const current = Number(prev) || 0;
                  const decrement = liveOfferData?.minimumBidDecrease || 0;
                  const highestBid =
                    bidPriceUpdateValue?.mimumestBid === 0
                      ? liveOfferData?.startingPrice
                      : bidPriceUpdateValue?.mimumestBid;
                  const updated = +(current - decrement).toFixed(10);
                  return Math.min(highestBid, updated);
                })
              }
            >
              <span
                className="white-text"
                style={{ fontSize: "30px", fontWeight: 600 }}
              >
                -
              </span>
            </button>
          </div>
          <p style={{ marginTop: "-8px" }}>
            Current Bid Amount : {roundToThreeDecimal(myCurrentBid) || 0}
          </p>
          <button
            className={`pointergrey lighten-2 grey-text white-text cercle-purple font-16px`}
            type="submit"
            style={{
              padding: "5px 20px",
              borderRadius: "20px",
            }}
            disabled={
              timeEnded || (loading && loadingId === liveOfferData?._id)
            }
          >
            {loading && loadingId === liveOfferData?._id ? (
              <ClipLoader color="#fff" size={20} />
            ) : (
              "Submit"
            )}
          </button>
        </form>
      </div>
    </div>
  );
}

export default ProductMobileBiddingArea;
